﻿/**
 * Sends an HTTP request and returns the response.
 * @param {string} url - The URL to send the request to.
 * @param {Object} options - Configuration options for the request.
 * @param {string} [options.method='GET'] - The HTTP method (e.g., 'GET', 'POST').
 * @param {Object} [options.headers={}] - Custom headers to include in the request.
 * @param {Object} [options.body=null] - The request body (for POST, PUT, etc.).
 * @returns {Promise<Object>} - The parsed JSON response.
 * @throws {Error} - If the request fails or the response is not OK.
 */
export async function sendRequest(url, options = {}) {
    const { method = 'GET', headers = {}, body = null } = options;

    // Set default headers
    const defaultHeaders = {
        'Content-Type': 'application/json',
    };

    // Merge custom headers with default headers
    const requestHeaders = {
        ...defaultHeaders,
        ...headers,
    };

    try {
        const response = await fetch(url, {
            method,
            headers: requestHeaders,
            body: body ? JSON.stringify(body) : null,
        });

        // Check if the response is OK (status code 200-299)
        if (!response.ok) {
            const errorData = await response.json().catch(() => null);
            throw new Error(
                `HTTP Error: ${response.status} ${response.statusText}. ${errorData?.message || ''}`
            );
        }

        // Parse and return the JSON response
        return await response.json();
    } catch (error) {
        console.error('Request Error:', error);
        throw error; // Re-throw the error for the caller to handle
    }
}

/**
 * Convenience function for sending a GET request.
 * @param {string} url - The URL to send the request to.
 * @param {Object} [headers={}] - Custom headers to include in the request.
 * @returns {Promise<Object>} - The parsed JSON response.
 */
export async function getRequest(url, headers = {}) {
    return sendRequest(url, { method: 'GET', headers });
}

/**
 * Convenience function for sending a POST request.
 * @param {string} url - The URL to send the request to.
 * @param {Object} body - The request body.
 * @param {Object} [headers={}] - Custom headers to include in the request.
 * @returns {Promise<Object>} - The parsed JSON response.
 */
export async function postRequest(url, body, headers = {}) {
    return sendRequest(url, { method: 'POST', headers, body });
}